/**
 * "Add to Home Screen", explained once.
 *
 * Aimed squarely at iPhone, where there is no install prompt to trigger and no
 * push notifications at all until the app is installed — so the coaching is
 * not a nicety, it is the only route to a working notification.
 *
 * Rules it follows, because an install nag is otherwise the most annoying
 * thing an app does:
 *   • Only on iOS, only in Safari, only when not already installed.
 *   • Never on the first visit — it waits until the reader has been back.
 *   • Dismissing it is permanent, stored on the account rather than the device.
 */

import { useEffect, useState } from 'react';
import { Share, SquarePlus, X } from 'lucide-react';

import { isIOS, isStandalone } from '../../lib/serviceWorker';
import { useSession } from '../../state/SessionProvider';

const VISITS_KEY = 'ss.visits';
const MIN_VISITS = 2;

export function InstallCoach() {
  const { prefs, savePrefs, status } = useSession();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (status !== 'ready') return;
    if (prefs.dismissedInstallPrompt) return;
    if (!isIOS() || isStandalone()) return;

    // Count visits rather than time-on-page: someone who has come back is
    // someone for whom an icon on the Home Screen is actually worth something.
    let visits = 0;
    try {
      visits = Number(localStorage.getItem(VISITS_KEY) ?? '0') + 1;
      localStorage.setItem(VISITS_KEY, String(visits));
    } catch {
      return;
    }

    if (visits < MIN_VISITS) return;

    // A beat after load, so it does not land on top of the first paint.
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, [status, prefs.dismissedInstallPrompt]);

  if (!visible) return null;

  const dismiss = (forever: boolean) => {
    setVisible(false);
    if (forever) void savePrefs({ dismissedInstallPrompt: true });
  };

  return (
    <div className="installcoach" role="dialog" aria-label="Add SamuelSchool to your Home Screen">
      <button
        type="button"
        className="icon-btn installcoach__close"
        onClick={() => dismiss(false)}
        aria-label="Not now"
      >
        <X size={18} />
      </button>

      <p className="installcoach__title">Keep SamuelSchool on your Home Screen</p>
      <p className="installcoach__body">
        It opens full screen, works offline, and it&rsquo;s the only way to get notifications on
        iPhone.
      </p>

      <ol className="installcoach__steps">
        <li>
          <Share size={15} aria-hidden="true" /> Tap Share
        </li>
        <li>
          <SquarePlus size={15} aria-hidden="true" /> Add to Home Screen
        </li>
      </ol>

      <button type="button" className="btn btn--ghost btn--sm" onClick={() => dismiss(true)}>
        Don&rsquo;t show again
      </button>
    </div>
  );
}
